/**
 * LoadingContext - Global Loading Manager
 * Manages multiple concurrent loading tasks with phases, cancellation, and retry
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const LoadingContext = createContext(null);

export const LoadingProvider = ({ children }) => {
  const [tasks, setTasks] = useState({}); // { taskId: Task }
  const nextId = useRef(0);
  const telemetryRef = useRef([]);

  // Generate unique task ID
  const generateId = useCallback(() => {
    return `task_${Date.now()}_${nextId.current++}`;
  }, []);

  // Log telemetry event
  const logTelemetry = useCallback((event) => {
    const entry = {
      ...event,
      timestamp: new Date().toISOString()
    };
    telemetryRef.current.push(entry);
    
    // Console banner for dev
    if (process.env.NODE_ENV === 'development') {
      const emoji = {
        start: '▶️',
        phase: '⏩',
        complete: '✅',
        fail: '❌',
        cancel: '⏹️'
      }[event.type] || '📊';
      
      console.log(
        `${emoji} [Progress] ${event.type.toUpperCase()}: ${event.label}`,
        event.meta ? ` | ${JSON.stringify(event.meta)}` : ''
      );
    }
  }, []);

  // Get the most recent active (running) task ID
  const activeTaskId = Object.keys(tasks).find(
    id => tasks[id].status === 'running'
  );

  // Start a new task
  const startTask = useCallback(({
    label,
    phases = [],
    canCancel = true,
    timeoutMs = 20000,
    meta = {}
  }) => {
    const taskId = generateId();
    const startedAt = performance.now();
    
    const task = {
      id: taskId,
      label,
      phases,
      currentPhaseIndex: 0,
      canCancel,
      timeoutMs,
      startedAt,
      status: 'running', // running | complete | failed | cancelled
      error: null,
      cancelCallback: null,
      meta
    };
    
    setTasks(prev => ({ ...prev, [taskId]: task }));
    
    logTelemetry({
      type: 'start',
      task_id: taskId,
      label,
      phases_count: phases.length,
      can_cancel: canCancel,
      meta
    });
    
    // Auto-update phase every few seconds if phases exist
    if (phases.length > 0) {
      const phaseInterval = Math.max(2000, timeoutMs / (phases.length + 1));
      const intervalId = setInterval(() => {
        setTasks(prev => {
          const t = prev[taskId];
          if (!t || t.status !== 'running') {
            clearInterval(intervalId);
            return prev;
          }
          
          const nextPhase = Math.min(t.currentPhaseIndex + 1, phases.length - 1);
          if (nextPhase !== t.currentPhaseIndex) {
            logTelemetry({
              type: 'phase',
              task_id: taskId,
              phase_index: nextPhase,
              phase_name: phases[nextPhase]
            });
          }
          
          return {
            ...prev,
            [taskId]: { ...t, currentPhaseIndex: nextPhase }
          };
        });
      }, phaseInterval);
      
      task.phaseIntervalId = intervalId;
    }
    
    return task;
  }, [generateId, logTelemetry]);

  // Update task phase manually
  const updateTask = useCallback((taskId, updates) => {
    setTasks(prev => {
      const task = prev[taskId];
      if (!task) return prev;
      
      const updated = { ...task, ...updates };
      
      // Log phase change
      if (updates.currentPhaseIndex !== undefined && 
          updates.currentPhaseIndex !== task.currentPhaseIndex) {
        logTelemetry({
          type: 'phase',
          task_id: taskId,
          phase_index: updates.currentPhaseIndex,
          phase_name: task.phases[updates.currentPhaseIndex]
        });
      }
      
      return { ...prev, [taskId]: updated };
    });
  }, [logTelemetry]);

  // Complete a task
  const completeTask = useCallback((taskId, { meta = {} } = {}) => {
    setTasks(prev => {
      const task = prev[taskId];
      if (!task) return prev;
      
      if (task.phaseIntervalId) {
        clearInterval(task.phaseIntervalId);
      }
      
      const durationMs = performance.now() - task.startedAt;
      
      logTelemetry({
        type: 'complete',
        task_id: taskId,
        label: task.label,
        duration_ms: Math.round(durationMs),
        meta: { ...task.meta, ...meta }
      });
      
      const updated = { ...task, status: 'complete' };
      
      // Auto-remove after 2 seconds
      setTimeout(() => {
        setTasks(prev => {
          const { [taskId]: _, ...rest } = prev;
          return rest;
        });
      }, 2000);
      
      return { ...prev, [taskId]: updated };
    });
  }, [logTelemetry]);

  // Fail a task
  const failTask = useCallback((taskId, { error, meta = {} } = {}) => {
    setTasks(prev => {
      const task = prev[taskId];
      if (!task) return prev;
      
      if (task.phaseIntervalId) {
        clearInterval(task.phaseIntervalId);
      }
      
      const durationMs = performance.now() - task.startedAt;
      
      logTelemetry({
        type: 'fail',
        task_id: taskId,
        label: task.label,
        duration_ms: Math.round(durationMs),
        error: error || 'Unknown error',
        meta: { ...task.meta, ...meta }
      });
      
      return {
        ...prev,
        [taskId]: { ...task, status: 'failed', error: error || 'Unknown error' }
      };
    });
  }, [logTelemetry]);

  // Cancel a task
  const cancelTask = useCallback((taskId) => {
    setTasks(prev => {
      const task = prev[taskId];
      if (!task) return prev;
      
      if (task.phaseIntervalId) {
        clearInterval(task.phaseIntervalId);
      }
      
      // Call registered cancel callback
      if (task.cancelCallback) {
        task.cancelCallback();
      }
      
      const durationMs = performance.now() - task.startedAt;
      
      logTelemetry({
        type: 'cancel',
        task_id: taskId,
        label: task.label,
        duration_ms: Math.round(durationMs)
      });
      
      // Auto-remove after 1 second
      setTimeout(() => {
        setTasks(prev => {
          const { [taskId]: _, ...rest } = prev;
          return rest;
        });
      }, 1000);
      
      return {
        ...prev,
        [taskId]: { ...task, status: 'cancelled' }
      };
    });
  }, [logTelemetry]);

  // Register cancel callback
  const registerCancelCallback = useCallback((taskId, callback) => {
    setTasks(prev => {
      const task = prev[taskId];
      if (!task) return prev;
      return {
        ...prev,
        [taskId]: { ...task, cancelCallback: callback }
      };
    });
  }, []);

  // Get telemetry for debugging
  const getTelemetry = useCallback(() => {
    return telemetryRef.current;
  }, []);

  const value = {
    tasks,
    activeTaskId,
    startTask,
    updateTask,
    completeTask,
    failTask,
    cancelTask,
    registerCancelCallback,
    getTelemetry
  };

  // Expose singleton for non-React usage (e.g., withProgress utility)
  useEffect(() => {
    window.__loadingManager = value;
    return () => {
      window.__loadingManager = null;
    };
  }, [tasks, activeTaskId]); // Depend on specific values, not entire value object

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within LoadingProvider');
  }
  return context;
};
